import { apiFetch } from './client';

export interface MatchedBecause {
  performers: string[];
  genres: string[];
}

export type PageParam = string | undefined;

interface CalendarArtist {
  name: string;
  disambiguation?: string;
  mbid?: string;
  image?: ArtistImage;
  bio?: ArtistBio;
  tour?: ArtistTour;
}

interface BioSource {
  kind: string;
  title: string;
  url: string;
  revision_id: number;
  mbid: string;
}

interface ArtistBio {
  text: string;
  sources?: BioSource[];
}

// Every field here is `omitempty` in internal/http/handlers/artist.go, so a
// tour can arrive carrying nothing but a blurb.
interface ArtistTour {
  name?: string;
  blurb?: string;
  setlist_url?: string;
  songs?: Array<{ name: string }>;
  observed?: TourObserved;
}

interface TourObserved {
  date?: string;
  venue?: string;
  city?: string;
}

// Every field but attribution_required is `omitempty` on the wire: Commons
// fills in whatever the uploader supplied and nothing more.
export interface ImageCredit {
  file?: string;
  description_url?: string;
  artist?: string;
  credit?: string;
  license?: string;
  license_short_name?: string;
  license_url?: string;
  usage_terms?: string;
  attribution_required: boolean;
}

interface ArtistImage {
  url: string;
  width: number;
  height: number;
  credit: ImageCredit;
}

export interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  starts_at: string;
  ends_at?: string;
  image_url?: string;
  url?: string;
  venue: { name: string; address?: string };
  // The source's top-level category slug. Absent when the source does not
  // classify its events, or when the row predates the field.
  segment?: string;
  score: number;
  matched_because: MatchedBecause;
  artist?: CalendarArtist;
}

export interface CalendarResponse {
  events: CalendarEvent[];
  // Omitted by the server (`json:"next_cursor,omitempty"`) on the last page,
  // which is what stops react-query from paging forever.
  next_cursor?: string;
}

// segment filters to that category. Events the source never classified come back
// under every value, so filtering never hides the pre-existing catalogue. The
// cursor carries only (starts_at, id), so segment must be re-sent on every page.
export async function getCalendar(cursor?: string): Promise<CalendarResponse> {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const params = cursor
    ? new URLSearchParams({ cursor })
    : new URLSearchParams({ starts_at: now.toISOString() });
  // Hardcoded for now
  params.set('segment', 'music');
  const result = await apiFetch<CalendarResponse>(
    `/me/calendar${params ? '?' + params.toString() : ''}`,
  );
  return result;
}

export async function getEvent(id: string): Promise<CalendarEvent> {
  return apiFetch<CalendarEvent>(`/events/${id}`);
}

// Every event in a city, unfiltered by match score. The calendar falls back to
// this when the user has nothing to match against yet. venueId narrows it to one
// venue; like segment, it is not in the cursor, so it is re-sent on every page.
export async function getCityCalendar(
  cityId: string,
  cursor?: string,
  venueId?: string,
): Promise<CalendarResponse> {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const params = cursor
    ? new URLSearchParams({ cursor })
    : new URLSearchParams({ starts_at: now.toISOString() });
  // Hardcoded for now
  params.set('segment', 'music');
  if (venueId) params.set('venue_id', venueId);
  const result = await apiFetch<CalendarResponse>(
    `/calendar/${cityId}${params ? '?' + params.toString() : ''}`,
  );
  return result;
}
